import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, fileUrl, auth as authLib, API } from "@/lib/api";
import { useBranding } from "@/lib/branding";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Download, ArrowLeft, Sparkles, Award, Activity, Target, HeartPulse, Printer, Loader2, FileBadge } from "lucide-react";
import { toast } from "sonner";
import html2canvas from "html2canvas";

const PERIODS = [
  { v: "q", label: "Quarterly" },
  { v: "h", label: "Half-yearly" },
  { v: "y", label: "Yearly" },
];

const Q_OPTIONS = [1, 2, 3, 4];
const H_OPTIONS = [1, 2];

export default function TrainingCard({ self = false }) {
  const { id: paramId } = useParams();
  const nav = useNavigate();
  const { branding, user } = useBranding();
  const brandColor = branding?.theme_color || "#0EA5E9";
  const id = self ? user?.emp_db_id : paramId;
  const [period, setPeriod] = useState("q");
  const [idx, setIdx] = useState(() => Math.floor(new Date().getMonth() / 3) + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const cardRef = useRef(null);

  async function load() {
    if (!id) return;
    const r = await api.get(`/training-card/${id}`, { params: { period, idx, year } });
    setData(r.data);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id, period, idx, year]);

  async function download() {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(cardRef.current, { scale: 2, backgroundColor: "#ffffff", useCORS: true, allowTaint: false });
      const a = document.createElement("a");
      a.href = canvas.toDataURL("image/png");
      a.download = `training-card-${data.employee.emp_id}-${data.period.label.replace(/\s+/g, "-")}.png`;
      a.click();
      toast.success("Card downloaded");
    } catch (e) {
      toast.error("Download failed");
    } finally { setDownloading(false); }
  }

  if (!data) return <div className="p-10 text-slate-500">Loading…</div>;

  const totals = data.totals;
  const tier = totals.avg_pct >= 85 ? { label: "Excellence", color: "#16a34a", emoji: "🏆" } :
               totals.avg_pct >= 70 ? { label: "Achiever", color: "#0ea5e9", emoji: "🌟" } :
               totals.avg_pct >= 50 ? { label: "Progressing", color: "#f59e0b", emoji: "📈" } :
                                       { label: "Just Starting", color: "#64748b", emoji: "🌱" };

  return (
    <div className="p-6 lg:p-10 max-w-5xl space-y-6 print:p-0 print:max-w-none print-tc-root">
      <style>{`
        @media print {
          @page { size: A4; margin: 12mm; }
          body { background: white !important; }
          /* Hide app chrome — sidebar / topbar / any nav-scoped nodes */
          aside, nav, [data-nav], [data-topbar], .no-print { display: none !important; }
          /* Keep only the training card visible; expand to page */
          .print-tc-root { padding: 0 !important; margin: 0 auto !important; max-width: 100% !important; }
          [data-testid="training-card"] { box-shadow: none !important; break-inside: avoid; }
          [data-testid="stat-total-points"] { display: none !important; }
        }
      `}</style>
      <Button variant="ghost" size="sm" onClick={()=>nav(self ? "/my" : "/employees")} className="print:hidden" data-testid="card-back"><ArrowLeft className="h-4 w-4 mr-1.5"/>Back</Button>

      <header className="flex flex-wrap items-end justify-between gap-4 print:hidden">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold" style={{ color: brandColor }}>Employee</div>
          <h1 className="font-display font-black text-3xl lg:text-4xl text-slate-900 mt-1">Training Card</h1>
          <p className="text-slate-500 mt-1">Performance snapshot for {data.employee.name} · {data.period.label}</p>
        </div>
        <div className="flex flex-wrap gap-2 items-end">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Period</Label>
            <Select value={period} onValueChange={(v)=>{
              setPeriod(v);
              if (v === "q") setIdx(Math.floor(new Date().getMonth() / 3) + 1);
              else if (v === "h") setIdx(new Date().getMonth() < 6 ? 1 : 2);
              else setIdx(1);
            }}>
              <SelectTrigger className="w-36" data-testid="period-select"><SelectValue/></SelectTrigger>
              <SelectContent>{PERIODS.map(p=><SelectItem key={p.v} value={p.v}>{p.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {period !== "y" && (
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">{period === "q" ? "Quarter" : "Half"}</Label>
              <Select value={String(idx)} onValueChange={(v)=>setIdx(parseInt(v))}>
                <SelectTrigger className="w-24" data-testid="idx-select"><SelectValue/></SelectTrigger>
                <SelectContent>{(period==="q"?Q_OPTIONS:H_OPTIONS).map(n=><SelectItem key={n} value={String(n)}>{period==="q"?`Q${n}`:`H${n}`}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Year</Label>
            <Select value={String(year)} onValueChange={(v)=>setYear(parseInt(v))}>
              <SelectTrigger className="w-24" data-testid="year-select"><SelectValue/></SelectTrigger>
              <SelectContent>{[year-1, year, year+1].map(y=><SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button onClick={download} disabled={downloading} className="text-white" style={{ background: brandColor }} data-testid="download-card">
            {downloading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin"/> : <Download className="h-4 w-4 mr-1.5"/>}PNG
          </Button>
          <Button variant="outline" onClick={()=>window.print()} data-testid="print-card"><Printer className="h-4 w-4 mr-1.5"/>Print</Button>
        </div>
      </header>

      {/* The actual card (capture target) */}
      <div ref={cardRef} className="relative rounded-3xl overflow-hidden border border-slate-200 shadow-lg bg-white" data-testid="training-card">
        {/* Decorative top band */}
        <div className="relative px-8 pt-8 pb-20 text-white overflow-hidden" style={{ background: `linear-gradient(135deg, ${brandColor}, ${tier.color})` }}>
          {/* SVG dots decoration */}
          <svg className="absolute inset-0 opacity-20" width="100%" height="100%">
            <defs><pattern id="dots" width="22" height="22" patternUnits="userSpaceOnUse"><circle cx="2" cy="2" r="2" fill="white"/></pattern></defs>
            <rect width="100%" height="100%" fill="url(#dots)"/>
          </svg>
          <div className="relative flex items-start justify-between">
            <div className="flex items-center gap-3">
              {branding?.logo_path ? (
                <img src={fileUrl(branding.logo_path)} alt="" className="h-14 w-14 rounded-2xl object-cover bg-white/15"/>
              ) : (
                <div className="h-14 w-14 rounded-2xl bg-white/15 backdrop-blur grid place-items-center"><HeartPulse className="h-7 w-7"/></div>
              )}
              <div>
                <div className="font-display font-black text-2xl leading-tight">{branding?.hospital_name || "MedLearn"}</div>
                <div className="text-xs uppercase tracking-[0.22em] text-white/80 font-bold">Training Card · {data.period.label}</div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-5xl leading-none">{tier.emoji}</div>
              <div className="text-xs uppercase tracking-[0.18em] text-white/80 font-bold mt-1">{tier.label}</div>
            </div>
          </div>
        </div>

        {/* Employee section, overlaps the band */}
        <div className="-mt-12 px-8 relative">
          <div className="bg-white rounded-2xl shadow-md border border-slate-100 p-5 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="h-16 w-16 rounded-2xl grid place-items-center font-display font-black text-2xl text-white" style={{ background: tier.color }}>
                {data.employee.name.split(" ").map(s=>s[0]).slice(0,2).join("")}
              </div>
              <div>
                <div className="font-display font-black text-2xl text-slate-900">{data.employee.name}</div>
                <div className="text-sm text-slate-500"><span className="font-mono font-bold" style={{ color: brandColor }}>{data.employee.emp_id}</span> · {data.employee.email}</div>
                <div className="text-xs text-slate-400 mt-0.5">{data.employee.department || "—"}{data.employee.sub_department ? " · " + data.employee.sub_department : ""}{data.employee.category ? " · " + data.employee.category : ""}</div>
              </div>
            </div>
            <div className="text-right">
              <div className="font-display font-black text-5xl" style={{ color: tier.color }}>{totals.avg_pct}%</div>
              <div className="text-xs uppercase tracking-wider text-slate-500 font-bold">Avg Score</div>
            </div>
          </div>
        </div>

        {/* Stats grid */}
        <div className="px-8 py-6 grid grid-cols-3 gap-3">
          <StatTile icon={Award} label="Completed" value={totals.completed_count} color="#16a34a"/>
          <StatTile icon={Activity} label="Attempts" value={totals.total_attempts} color={brandColor}/>
          <StatTile icon={Target} label="Total Points" value={`${totals.total_score}/${totals.total_max}`} color="#8b5cf6" className="print:hidden" testid="stat-total-points"/>
        </div>

        {/* Trainings list */}
        <div className="px-8 pb-6">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">Trainings Completed</div>
          {data.trainings.length === 0 && (
            <div className="text-sm text-slate-400 italic py-6 text-center">No training completed in this period.</div>
          )}
          <div className="space-y-2">
            {data.trainings.map((t, i) => (
              <div key={i} className="flex items-center justify-between p-3 rounded-xl border border-slate-100 bg-slate-50/50">
                <div>
                  <div className="font-bold text-slate-900">{t.title}</div>
                  <div className="text-xs text-slate-500">{t.topic} · {new Date(t.completed_at).toLocaleDateString()}</div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <div className="font-bold">{t.score}/{t.max_score}</div>
                    <div className={`text-xs font-bold ${t.percentage>=85?"text-green-600":t.percentage>=70?"text-sky-600":t.percentage>=50?"text-amber-600":"text-red-600"}`}>{t.percentage}%</div>
                  </div>
                  {t.attempt_id && (
                    <Button variant="outline" size="sm" className="h-8" onClick={async ()=>{
                      try {
                        const resp = await fetch(`${API}/attempts/${t.attempt_id}/certificate.pdf`, {
                          headers: { Authorization: `Bearer ${authLib.token()}` },
                        });
                        if (!resp.ok) throw new Error("Certificate failed");
                        const blob = await resp.blob();
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = url; a.download = `certificate-${t.title.replace(/[^a-z0-9]+/gi,'-')}.pdf`; a.click();
                        URL.revokeObjectURL(url);
                      } catch (e) { toast.error("Could not download certificate"); }
                    }} data-testid={`cert-${i}`}>
                      <FileBadge className="h-3.5 w-3.5 mr-1"/>Certificate
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer ribbon */}
        <div className="px-8 py-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-400">
          <div className="inline-flex items-center gap-1"><Sparkles className="h-3 w-3" style={{ color: brandColor }}/> Generated {new Date().toLocaleDateString()}</div>
          <div>{branding?.hospital_name || "MedLearn"} Training Program</div>
        </div>
      </div>
    </div>
  );
}

function StatTile({ icon: Icon, label, value, color, className = "", testid }) {
  return (
    <div className={`rounded-2xl border border-slate-100 bg-white p-4 ${className}`} data-testid={testid}>
      <div className="h-9 w-9 rounded-xl grid place-items-center" style={{ background: `${color}15`, color }}>
        <Icon className="h-5 w-5"/>
      </div>
      <div className="font-display font-black text-2xl text-slate-900 mt-2">{value}</div>
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">{label}</div>
    </div>
  );
}
